import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const aud = (n) => `A$${Number(n || 0).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;

const Stat = ({ label, value, testId, sub }) => (
  <div className="border-t-2 border-[#3B1B48] pt-4">
    <p className="eyebrow">{label}</p>
    <p data-testid={testId} className="font-display text-5xl text-[#3B1B48] mt-2">{value}</p>
    {sub && <p data-testid={`${testId}-split`} className="text-xs text-[#8C827A] mt-1">{sub}</p>}
  </div>
);

const DayChart = ({ days }) => {
  const max = Math.max(1, ...days.map((d) => d.orders));
  return (
    <div data-testid="sales-chart" className="bg-[#F4EFE6] border border-[#E2D9CC] p-8">
      <p className="eyebrow mb-6">Orders · last 30 days</p>
      <div className="flex items-end gap-1 h-40">
        {days.map((d) => (
          <div key={d.date} className="flex-1 group relative h-full flex items-end">
            <div className="w-full bg-[#3B1B48] group-hover:bg-[#C59B27] transition-colors" style={{ height: `${(d.orders / max) * 100}%`, minHeight: d.orders ? 4 : 1, opacity: d.orders ? 1 : 0.15 }} />
            <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-[10px] font-mono-accent bg-[#1C181D] text-[#FAF7F2] px-1.5 py-0.5 opacity-0 group-hover:opacity-100 whitespace-nowrap pointer-events-none">{d.date.slice(5)} · {d.orders}</span>
          </div>
        ))}
      </div>
      <div className="flex justify-between text-[10px] font-mono-accent text-[#8C827A] mt-2"><span>{days[0]?.date.slice(5)}</span><span>today</span></div>
    </div>
  );
};

export default function SalesPage() {
  const [d, setD] = useState(null);
  const last = useRef(undefined);
  useEffect(() => {
    const load = () => api.get("/sales").then((r) => {
      if (last.current !== undefined && r.data.latest_order_at && r.data.latest_order_at !== last.current) {
        const o = r.data.recent[0];
        toast.success(`New order: ${o.pack_name || o.town}${o.amount != null ? ` · ${aud(o.amount)}` : ""}`);
      }
      last.current = r.data.latest_order_at;
      setD(r.data);
    }).catch((e) => toast.error(errMsg(e)));
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);
  if (!d) return <p className="eyebrow">Loading sales…</p>;
  const t = d.totals;

  return (
    <div className="rise">
      <p className="eyebrow mb-3">theexpatstudio.com</p>
      <h1 className="font-display text-4xl sm:text-5xl text-[#3B1B48] mb-4">Sales</h1>
      <p className="text-[#8C827A] mb-12 max-w-2xl">Every purchase appears here the moment your shop asks for the buyer's download links. Revenue shows when your site sends the order amount.
        <span data-testid="sales-live-indicator" className="ml-3 inline-flex items-center gap-1.5 text-xs text-[#2D6A4F]"><i className="w-2 h-2 rounded-full bg-[#2D6A4F] animate-pulse inline-block" />Live · refreshes every 15s</span></p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 mb-14">
        <Stat label="Orders" value={t.orders} testId="sales-orders" />
        <Stat label="Revenue" value={aud(t.revenue)} testId="sales-revenue" sub={`Small towns ${aud(t.town_revenue)} · Relocation ${aud(t.relocation_revenue)}`} />
        <Stat label="Buyers" value={t.buyers} testId="sales-buyers" />
        <Stat label="Downloads" value={t.downloads} testId="sales-downloads" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-10 mb-14">
        <DayChart days={d.by_day} />
        <div className="bg-[#F4EFE6] border border-[#E2D9CC] p-8" data-testid="sales-by-guide">
          <p className="eyebrow mb-4">By guide</p>
          {d.by_guide.length === 0 && <p className="text-sm text-[#8C827A]">No sales yet.</p>}
          {d.by_guide.map((r) => (
            <Link key={r.guide_id} to={`/guides/${r.guide_id}`} className="flex items-center gap-4 py-3 border-b border-[#E2D9CC] last:border-0 hover:bg-[#EFE6F3]/60 transition-colors">
              <span className="font-display text-xl text-[#C59B27] w-8">{r.issue_number ?? "—"}</span>
              <span className="flex-1 font-display text-[#3B1B48]">{r.town}<span className="block text-xs font-sans text-[#8C827A]">{r.orders} orders · {r.bundles} bundles · {r.downloads} downloads</span></span>
              <span className="font-display text-lg text-[#3B1B48]">{aud(r.revenue)}</span>
            </Link>
          ))}
        </div>
      </div>
      <div className="bg-[#F4EFE6] border border-[#E2D9CC] p-8 mb-14" data-testid="sales-relocation">
        <p className="eyebrow mb-4">Relocation guides · {aud(t.relocation_revenue)}</p>
        {d.relocation.length === 0 && <p className="text-sm text-[#8C827A]">No relocation sales yet.</p>}
        {d.relocation.map((r) => (
          <Link key={r.guide_id} to={`/relocation/${r.guide_id}`} data-testid={`sales-reloc-${r.guide_id}`} className="flex items-center gap-4 py-3 border-b border-[#E2D9CC] last:border-0 hover:bg-[#EFE6F3]/60 transition-colors">
            <span className="flex-1 font-display text-[#3B1B48]">Australia → {r.destination}<span className="block text-xs font-sans text-[#8C827A]">{r.orders} sales · {Object.entries(r.products).map(([k, n]) => `${k} ×${n}`).join(" · ")}</span></span>
            <span className="font-display text-lg text-[#3B1B48]">{aud(r.revenue)}</span>
          </Link>
        ))}
      </div>
      <div className="bg-[#FAF7F2] border border-[#E2D9CC] overflow-x-auto" data-testid="sales-recent">
        <div className="min-w-[820px] grid grid-cols-[1.2fr_1.6fr_1fr_100px_100px_1fr] px-6 py-4 border-b border-[#E2D9CC] eyebrow !text-[#8C827A]">
          <span>Order</span><span>Buyer</span><span>Guide</span><span>Variant</span><span>Amount</span><span>Date</span>
        </div>
        {d.recent.length === 0 && <p className="p-6 text-sm text-[#8C827A]">No orders yet.</p>}
        {d.recent.map((o) => (
          <div key={o.order_id + o.created_at} className="min-w-[820px] grid grid-cols-[1.2fr_1.6fr_1fr_100px_100px_1fr] px-6 py-3 border-b border-[#E2D9CC] last:border-0 text-sm">
            <span className="font-mono-accent text-xs">{o.order_id}</span><span className="truncate pr-3">{o.email || "—"}</span><span data-testid={`sales-recent-guide-${o.order_id}`}>{o.town || "—"}{o.pack_name && <span className="block text-[11px] text-[#C59B27]">{o.pack_name}</span>}</span>
            <span className="capitalize truncate pr-2">{o.relocation && <span className="block text-[10px] uppercase tracking-wider text-[#C59B27]">Relocation</span>}{o.variant || "—"}</span><span>{o.amount != null ? aud(o.amount) : "—"}</span>
            <span className="text-[#8C827A]">{new Date(o.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
